import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import { AppAvatar } from '@src/app/components'
import { useCallsDialogContext } from '@src/app/voice/context/useCallsDialogContext'
import { useCallContext } from '@src/app/voice/ongoing-call/context/useCallContext'
import type { CallTransferState } from '@src/app/voice/store/CallTransferController'
import Button from '@src/component/ButtonV2'
import type { Identity } from '@src/service/model'
import Typography from '@ui/Typography'
import { CallLeaveIcon } from '@ui/icons/tint/20/general'

import * as styles from './CallTransferStatus.css'

const getTitle = (mode: CallTransferState, identity: Identity | null): string => {
  switch (mode) {
    case 'in-progress':
      return `Transferring to ${identity?.name}`
    case 'success':
      return 'This call has been transferred'
    case 'failed':
      return 'Transfer failed'
  }
}

const getDescription = (mode: CallTransferState): string => {
  switch (mode) {
    case 'in-progress':
      return 'You can leave the call now, or at any point after someone picks up.'
    case 'success':
      return 'You can leave the call now, or stick around if you wish.'
    case 'failed':
      return 'Unfortunately no one picked up. You can try again, or choose someone else to transfer to.'
  }
}

const CallTransferStatus = () => {
  const { blurred } = useCallsDialogContext()
  const { transferController: controller } = useCallContext()

  if (controller.transferMode !== 'transfer') {
    return null
  }

  return (
    <div className={styles.root}>
      <AppAvatar
        identity={controller.transferee?.identity}
        size={80}
        circleClassName={cx(styles.avatar, {
          [styles.pulse]: controller.transferState === 'in-progress',
          [styles.failed]: controller.transferState === 'failed',
          [styles.success]: controller.transferState === 'success',
        })}
      />
      <Typography variant="body" color="textPrimary" className={styles.title}>
        {controller.transferState
          ? getTitle(controller.transferState, controller.transferee?.identity ?? null)
          : null}
      </Typography>
      <Typography variant="callout" color="textSecondary" className={styles.description}>
        {controller.transferState ? getDescription(controller.transferState) : null}
      </Typography>
      {controller.transferState === 'failed' ? (
        <div className={styles.buttons}>
          {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
          <Button
            color="textPrimary"
            variant="ghost"
            onClick={controller.handleTransfer}
            className={blurred ? styles.buttonBlurred : undefined}
          >
            Try again
          </Button>
          {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
          <Button
            color="textPrimary"
            variant="ghost"
            onClick={controller.handleBack}
            className={blurred ? styles.buttonBlurred : undefined}
          >
            Someone else
          </Button>
        </div>
      ) : (
        // eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button
        <Button
          color="primary"
          startIcon={<CallLeaveIcon />}
          onClick={controller.handleLeaveCall}
          className={blurred ? styles.buttonBlurred : undefined}
        >
          Leave call
        </Button>
      )}
    </div>
  )
}

export default observer(CallTransferStatus)
